import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Calculator, Building, Wrench, FileSpreadsheet, Edit, 
  Plane, Train, Pickaxe, Fuel, Hospital, School, Factory, 
  Database, Globe, Shield, TestTube, AlertTriangle, Crown,
  Link, ExternalLink, RefreshCw, Settings, BarChart3,
  DollarSign, Percent, Hash, Users, Truck, Zap, FileText
} from "lucide-react";
// Types for the bill of quantities system
interface CostComponent {
  id: string;
  name: string;
  description: string;
  orderIndex: number;
  isActive: boolean;
}

interface BillItemCostComponent {
  id: string;
  billItemId: string;
  componentType: string;
  componentName: string;
  quantity: number;
  unitRate: number;
  totalCost: number;
  calculationOrder: number;
  isOverride: boolean;
  notes?: string;
}

interface BillItem {
  id: string;
  besmm4Code: string;
  name: string;
  description?: string;
  unitOfMeasure: string;
  category: string;
  rate?: number;
  quantity?: number;
  amount?: number;
  calculationMethod: 'material' | 'composite' | 'fixed';
  calculationStatus: 'pending' | 'calculated' | 'error';
}

// New BOQ Template interfaces
interface BOQTemplate {
  id: string;
  templateName: string;
  templateVersion: string;
  description: string;
  isActive: boolean;
}

interface BOQTemplateItem {
  id: string;
  besmm4Code: string;
  besmm4Description: string;
  category: string;
  subcategory: string;
  unitOfMeasure: string;
  estimatedQuantity: number;
  estimatedRate: number;
  estimatedAmount: number;
  notes: string;
  itemOrder: number;
}

const BillOfQuantitiesModule = () => {
  const [activeTab, setActiveTab] = useState("bill-items");
  const [selectedBillItem, setSelectedBillItem] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const [selectedBOQTemplate, setSelectedBOQTemplate] = useState<string>("all");

  // Sample data for cost components
  const [costComponents] = useState<CostComponent[]>([
    { id: "1", name: "Material Cost", description: "Cost of materials delivered to site", orderIndex: 1, isActive: true },
    { id: "2", name: "Labour Cost", description: "Cost of labour required to execute the work", orderIndex: 2, isActive: true },
    { id: "3", name: "Plant and Equipment Cost", description: "Cost of using plant and equipment", orderIndex: 3, isActive: true },
    { id: "4", name: "Transportation Cost", description: "Cost of transportation and logistics", orderIndex: 4, isActive: true },
    { id: "5", name: "Overheads and Profit", description: "Site and head office overheads plus profit margin", orderIndex: 5, isActive: true },
    { id: "6", name: "Insurance", description: "Various insurance coverages required", orderIndex: 6, isActive: true },
    { id: "7", name: "Testing and Quality Control", description: "Material testing and quality assurance", orderIndex: 7, isActive: true },
    { id: "8", name: "Contingencies and Risk Allowance", description: "Risk factors and contingencies", orderIndex: 8, isActive: true },
    { id: "9", name: "Royalties and Levies", description: "Government levies and statutory fees", orderIndex: 9, isActive: true },
  ]);


  // Sample bill items
  const [billItems] = useState<BillItem[]>([
    { id: "1", besmm4Code: "A10", name: "Concrete in Foundations", description: "Concrete mix 1:2:4 in foundations", unitOfMeasure: "m³", category: "Concrete Work", calculationMethod: "composite", calculationStatus: "calculated" },
    { id: "2", besmm4Code: "A20", name: "Reinforced Concrete Columns", description: "Reinforced concrete columns 300x300mm", unitOfMeasure: "m³", category: "Concrete Work", calculationMethod: "composite", calculationStatus: "calculated" },
    { id: "3", besmm4Code: "B10", name: "Blockwork in Walls", description: "Blockwork in walls 225mm thick", unitOfMeasure: "m²", category: "Masonry", calculationMethod: "composite", calculationStatus: "calculated" },
    { id: "4", besmm4Code: "C10", name: "Steel Reinforcement", description: "Steel reinforcement in concrete", unitOfMeasure: "kg", category: "Reinforcement", calculationMethod: "material", calculationStatus: "calculated" },
  ]);

  // Sample bill item cost components
  const [billItemCostComponents] = useState<BillItemCostComponent[]>([
    { id: "1", billItemId: "1", componentType: "material", componentName: "Portland Cement", quantity: 350, unitRate: 4500, totalCost: 1575000, calculationOrder: 1, isOverride: false },
    { id: "2", billItemId: "1", componentType: "labor", componentName: "Mason", quantity: 8, unitRate: 2500, totalCost: 20000, calculationOrder: 2, isOverride: false },
    { id: "3", billItemId: "1", componentType: "equipment", componentName: "Concrete Mixer", quantity: 2, unitRate: 15000, totalCost: 30000, calculationOrder: 3, isOverride: false },
    { id: "4", billItemId: "2", componentType: "material", componentName: "Steel Reinforcement", quantity: 120, unitRate: 850, totalCost: 102000, calculationOrder: 1, isOverride: false },
    { id: "5", billItemId: "2", componentType: "labor", componentName: "Steel Fixer", quantity: 6, unitRate: 2800, totalCost: 16800, calculationOrder: 2, isOverride: false },
  ]);

  // Sample BOQ Templates
  const [boqTemplates] = useState<BOQTemplate[]>([
    { id: "1", templateName: "BOQ Template 01", templateVersion: "1.0", description: "Standard BOQ template based on BESMM4 for substructure, frames, and upper floors", isActive: true },
  ]);

  // Sample BOQ Template Items (this would come from the database after running migrations)
  const [boqTemplateItems] = useState<BOQTemplateItem[]>([
    { id: "1", besmm4Code: "2.1.1", besmm4Description: "Girth 500 - 1500mm; Filled with laterite", category: "SUBSTRUCTURE", subcategory: "EXCAVATING AND FILLING", unitOfMeasure: "Nr.", estimatedQuantity: 1, estimatedRate: 50000, estimatedAmount: 50000, notes: "Tree removal and filling with laterite", itemOrder: 1 },
    { id: "2", besmm4Code: "4.1.1", besmm4Description: "Site Clearance; Clearing Site vegetation and other growth and dispose off site; entire site", category: "SUBSTRUCTURE", subcategory: "EXCAVATING AND FILLING", unitOfMeasure: "Sq.m", estimatedQuantity: 1000, estimatedRate: 150, estimatedAmount: 150000, notes: "Site clearance and vegetation removal", itemOrder: 2 },
    { id: "3", besmm4Code: "6.2.1", besmm4Description: "Excavation, commencing from stripped level; Foundation excavation; Not exceeding 2m deep", category: "SUBSTRUCTURE", subcategory: "EXCAVATING AND FILLING", unitOfMeasure: "Cu.m", estimatedQuantity: 500, estimatedRate: 2500, estimatedAmount: 1250000, notes: "Foundation excavation not exceeding 2m deep", itemOrder: 3 },
    { id: "4", besmm4Code: "1.1.2/1", besmm4Description: "Mass Concrete; 225mm thick; In trench filling; Poured on or against earth or unblinded hardcore", category: "SUBSTRUCTURE", subcategory: "INSITU CONCRETE WORKS", unitOfMeasure: "Cu.m", estimatedQuantity: 100, estimatedRate: 45000, estimatedAmount: 4500000, notes: "Mass concrete 225mm thick in trench filling", itemOrder: 4 },
    { id: "5", besmm4Code: "34.1.2", besmm4Description: "High yield steel bars; 20mm diameter; Bent", category: "SUBSTRUCTURE", subcategory: "REINFORCEMENT", unitOfMeasure: "Tonnes", estimatedQuantity: 15, estimatedRate: 450000, estimatedAmount: 6750000, notes: "20mm diameter high yield steel bars", itemOrder: 5 },
  ]);


  // Filter bill item cost components based on selected bill item
  const filteredBillItemComponents = billItemCostComponents.filter(comp => 
    selectedBillItem === "all" || comp.billItemId === selectedBillItem
  );

  // Filter BOQ template items based on selected template
  const filteredBOQTemplateItems = boqTemplateItems.filter(item => 
    selectedBOQTemplate === "all" || item.id === selectedBOQTemplate
  );



  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Bill of Quantity Management</h1>
          <p className="text-muted-foreground">
            Manage bill items and BOQ templates
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="bill-items">Bill Item Components</TabsTrigger>
          <TabsTrigger value="boq-templates">BOQ Templates</TabsTrigger>
        </TabsList>

        {/* Bill Item Components Tab */}
        <TabsContent value="bill-items" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5" />
                Bill Item Cost Components
              </CardTitle>
              <CardDescription>
                View and manage cost components for specific bill items
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 mb-4">
                <Select value={selectedBillItem} onValueChange={setSelectedBillItem}>
                  <SelectTrigger className="w-[250px]">
                    <SelectValue placeholder="Filter by bill item" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Bill Items</SelectItem>
                    {billItems.map(item => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.besmm4Code} - {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ref No</TableHead>
                    <TableHead>Bill Item</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Unit Rate</TableHead>
                    <TableHead>Total Cost</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBillItemComponents.map((component) => {
                    const billItem = billItems.find(item => item.id === component.billItemId);
                    
                    return (
                      <TableRow key={component.id}>
                        <TableCell>
                          <Badge variant="outline" className="font-mono">
                            {billItem?.besmm4Code}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{billItem?.besmm4Code}</div>
                            <div className="text-sm text-muted-foreground">{billItem?.name}</div>
                          </div>
                        </TableCell>
                        <TableCell>{component.quantity}</TableCell>
                        <TableCell>₦{component.unitRate.toLocaleString()}</TableCell>
                        <TableCell>₦{component.totalCost.toLocaleString()}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm">
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* BOQ Templates Tab */}
        <TabsContent value="boq-templates" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                BOQ Template 01 - Standard Template
              </CardTitle>
              <CardDescription>
                Bill of Quantities template based on BESMM4 codes from the CSV template
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 mb-4">
                <Select value={selectedBOQTemplate} onValueChange={setSelectedBOQTemplate}>
                  <SelectTrigger className="w-[250px]">
                    <SelectValue placeholder="Filter by template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Templates</SelectItem>
                    {boqTemplates.map(template => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.templateName} v{template.templateVersion}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ref No</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Rate (₦)</TableHead>
                    <TableHead>Amount (₦)</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBOQTemplateItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">
                          {item.besmm4Code}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{item.besmm4Description}</div>
                          <div className="text-sm text-muted-foreground">{item.notes}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{item.category}</div>
                          <div className="text-sm text-muted-foreground">{item.subcategory}</div>
                        </div>
                      </TableCell>
                      <TableCell>{item.unitOfMeasure}</TableCell>
                      <TableCell>{item.estimatedQuantity.toLocaleString()}</TableCell>
                      <TableCell>₦{item.estimatedRate.toLocaleString()}</TableCell>
                      <TableCell>₦{item.estimatedAmount.toLocaleString()}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm">
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

    </div>
  );
};

export default BillOfQuantitiesModule;
